import Image from "./Image";
import { useInshurLanding, CardLink } from "../landingMode";
import { ArrowRight, Play } from "lucide-react";
import { TelematicsLogoCollage, PaymentsCarousel, TollsMapVertical, AgreementsBuilder } from "./FeatureCardMedia";
import { CrmChatPreview } from "./CrmChatPreview";
import { FleetVehiclePreview } from "./FleetVehiclePreview";
import { RentAiAgentPreview } from "./RentaiAgentPreview";
import { RentSuccessBackOfficeTree } from "./RentsuccessBackofficeTree";
import { RentidMarketplaceCard } from "./RentidMarketplaceCard";
import { FeatureCard } from "./FeatureCard";
import { FeatureAccordion } from "./FeatureAccordion";
import { InshurClaimCta } from "./InshurClaimCta";
/* rentAI is featured on its own full-bleed colored band (media-top / text-bottom card),
   separate from the two stacked cards below it. */
const RENTAI = {
    href: "/rentai",
    logo: "/logos/products/rentai-white.png",
    logoAlt: "rentAI",
    /* Vivid brand band: bright blue-purple at the top fading to near-black toward the card. */
    band: "radial-gradient(130% 120% at 50% -25%, #3D5BFF 0%, #2731C2 34%, #141863 60%, #0A0B24 82%, #050505 100%)",
    glow: "rgba(61,139,255,0.28)",
    title: "Meet Rentii. Your AI rental agent to manage your day-to-day.",
    lead: "AI native platform & context aware rental agents",
};
const RENTSUCCESS = {
    href: "/rentsuccess",
    logo: "/logos/products/rentsuccess-white.png",
    logoAlt: "rentSuccess",
    gradient: "radial-gradient(115% 95% at 90% 88%, #8A5CFF 0%, #5A2FB0 32%, #23124A 66%, #050505 100%)",
    glow: "rgba(138,92,255,0.30)",
    title: "Rental back office & resolution center",
    copy: "Rental workflows, tracking, incidental claims, agreement violations and resolutions, completely automated.",
};
/* Suite brand marks (icon-only, no wordmark) shown as an overlapping stack of black-glass
   circles in the rentAI band header, signalling the products the AI agent orchestrates. */
const SUITE_MARKS = [
    { src: "/images/rentid-suite/rentdirect-mark.png", alt: "rentDirect" },
    { src: "/images/rentid-suite/rentshield-mark.png", alt: "rentShield" },
    { src: "/images/rentid-suite/rentid-mark.png", alt: "rentID" },
    { src: "/images/rentid-suite/rentsuccess-mark.svg", alt: "rentSuccess" },
];
function SuiteMarksRow({ showMarks = true, showLearnMore }) {
    const inshur = useInshurLanding();
    const learnMore = showLearnMore ?? !inshur;
    return (<div className="flex flex-wrap items-center gap-x-5 gap-y-3">
      <div className={showMarks ? "flex items-center" : "hidden"}>
        {SUITE_MARKS.map((mark, i) => (<span key={mark.alt} className="relative -ml-3 flex h-12 w-12 items-center justify-center rounded-full bg-black/40 ring-1 ring-white/15 backdrop-blur-sm first:ml-0" style={{ zIndex: SUITE_MARKS.length - i }}>
            <Image src={mark.src || "/placeholder.svg"} alt={mark.alt} width={48} height={48} className="h-7 w-7 object-contain"/>
          </span>))}
      </div>
      {learnMore && (<a href={RENTAI.href} className="group/lm inline-flex shrink-0 items-center gap-2 text-base font-normal text-white sm:text-lg">
        <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover/lm:decoration-white">
          Learn more
        </span>
        <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
      </a>)}
    </div>);
}
const CARDS = [
    {
        key: "rentacademy",
        href: "/rentacademy",
        logo: "/logos/products/rentacademy-white.png",
        logoAlt: "rentAcademy",
        gradient: "radial-gradient(115% 95% at 90% 88%, #FFB020 0%, #C25812 32%, #3C2308 66%, #050505 100%)",
        glow: "rgba(255,176,32,0.28)",
        title: "Scale responsibly with ongoing education",
        copy: "Guided content, webinars, and training from industry experts.",
    },
];
/* Six platform-capability cards. These are features rather than sub-brands, so they share one
   restrained neutral-dark treatment with a teal accent instead of six competing brand colors. */
const FEATURES = [
    {
        key: "telematics",
        href: "https://rentshield.io/telematics",
        title: "Telematics, integrated.",
        copy: "Track vehicles in real time with your approved GPS device, integrated directly into your account.",
        preview: <TelematicsLogoCollage />,
    },
    {
        key: "payments",
        href: "/rentdirect#FinancialManagement",
        title: "Payments, streamlined.",
        copy: "Take Credit cards with 30-day Pre-authorization payments, Venmo, CashApp, PayPal with next day instant payouts.",
        preview: <PaymentsCarousel />,
    },
    {
        key: "fleet",
        href: "/rentdirect#FleetManagementCenter",
        title: "Fleet, managed.",
        copy: "Track availability, maintenance and utilization and analytics across your whole fleet.",
        preview: <FleetVehiclePreview />,
    },
    {
        key: "agreements",
        href: "/rentdirect#financialManagements_2", // Dynamic Agreements tab (owner 2026-10-02)
        title: "Agreements, customized.",
        copy: "Dynamic, customizable, signature ready rental agreements you can depend on. ",
        preview: <AgreementsBuilder />,
    },
    {
        key: "crm",
        href: "/rentdirect#CustomerRelationshipTools",
        title: "CRM, built in.",
        copy: "Every renter, conversation and booking in one place so nothing slips through.",
        preview: <CrmChatPreview />,
    },
    {
        key: "tolls",
        href: "https://rentshield.io/tolls-tickets",
        title: "Tolls, automated.",
        copy: "Automatic toll and ticket capture, matched to the trip and billed back with zero admin.",
        preview: <TollsMapVertical />,
    },
];
const RENTAI_VIDEO_CARD = false;
const ALL_SECTIONS = ["success", "features", "ai", "academy"];
/* Props (v0 2026-10-13, for /tnt-group): `sections` picks which of the four blocks render,
   hideLearnMore drops every "Learn more" (defaults to the INSHUR landing mode), wide uses the
   rentShield card's gutters and matchLogos scales the lockups to match rentShield / rentDirect. */
export function HomeProductTrio({ sections = ALL_SECTIONS, hideLearnMore, wide = false, matchLogos = false } = {}) {
    const inshur = useInshurLanding(); // /inshur: no links off the page
    const hideLM = hideLearnMore ?? inshur;
    const show = (s) => sections.includes(s);
    const containerClass = `mx-auto w-full max-w-7xl ${wide ? "px-4 sm:px-6" : "px-5 sm:px-8 md:px-16"}`;
    const logoSize = matchLogos ? "h-[54px] sm:h-[68px]" : "h-16 sm:h-20";
    return (<section className="w-full px-4 sm:px-6 md:px-16" aria-label="More from the RentID platform">
      {/* rentSuccess — sits above the rentAI band, mirroring the rentID (HomeAgentic) section:
            a centered max-w-7xl card with the brand lockup, heading and copy on the left and a
            portrait video player on the right. Broken out of the section's px gutter so its
            widths match the rentID section exactly. */}
      {show("success") && (
      <div id="rent-success" className="relative left-1/2 mb-4 w-screen -translate-x-1/2 scroll-mt-24">
        <div className={containerClass}>
          <CardLink href={RENTSUCCESS.href} className="group relative isolate block overflow-hidden rounded-2xl border-t border-white/15 transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:rounded-3xl" style={{ backgroundColor: "#050505", backgroundImage: RENTSUCCESS.gradient }}>
            <div className="relative z-10 grid gap-8 p-6 sm:p-10 lg:grid-cols-2 lg:gap-6 lg:p-14">
              {/* Left column — brand mark on top, heading + copy below. */}
              <div className="flex flex-col">
                <Image src={RENTSUCCESS.logo || "/placeholder.svg"} alt={RENTSUCCESS.logoAlt} width={200} height={92} className={`w-auto object-contain object-left ${logoSize}`}/>
                <div className="mt-8 sm:mt-10 lg:mt-12">
                  <h2 className="font-sans text-3xl font-normal sm:text-4xl md:text-[43.6px] leading-[1.05] tracking-tight text-white text-balance">
                    {RENTSUCCESS.title}
                  </h2>
                  <p className="mt-6 max-w-md font-sans text-lg leading-relaxed text-white/60 text-pretty">
                    {RENTSUCCESS.copy}
                  </p>
                  {!hideLM && (
                  <span className="mt-6 inline-flex items-center gap-2 align-middle text-base font-normal text-white sm:text-lg">
                    <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
                      Learn more
                    </span>
                    <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
                  </span>
                  )}
                </div>
              </div>

              {/* Right column — connected back-office beam diagram. */}
              <div className="relative flex items-center justify-center lg:items-start lg:justify-end">
                <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 60% at 60% 40%, ${RENTSUCCESS.glow}, transparent 70%)` }}/>
                <div className="relative w-full">
                  <RentSuccessBackOfficeTree />
                </div>
              </div>
            </div>
          </CardLink>
        </div>
      </div>
      )}

      {/* Platform-capability features — a bare grid of six cards sitting directly beneath the
            rentSuccess section (no container background, heading or subcopy). Purple accent matched
            to the rentSuccess brand color. */}
      {show("features") && (
      <div className="relative left-1/2 mb-[3.75rem] w-screen -translate-x-1/2 sm:mb-[4.5rem] lg:mb-[5.25rem]">
        <div className={containerClass}>
          <div className="sm:hidden">
            <FeatureAccordion features={FEATURES}/>
          </div>
          <div className="hidden gap-4 sm:grid sm:grid-cols-2 lg:grid-cols-3">
            {FEATURES.map((feature) => (<FeatureCard key={feature.key} href={feature.href} title={feature.title} copy={feature.copy} preview={feature.preview} 
        /* Payments keeps looping exactly as before; every other card plays only when active. */
        gated={feature.key !== "payments"} hideLearnMore={hideLM}/>))}
          </div>
        </div>
      </div>
      )}

      {/* rentAI — featured on its own full-bleed colored band that runs edge-to-edge across
            the viewport (breaking out of the centered content column). A large brand lockup and
            heading sit at the top of the band; the card itself stacks a media preview on top of
            the title + copy at the bottom. */}
      {show("ai") && (
      <div id="rent-ai" className="relative left-1/2 mb-20 w-screen scroll-mt-24 -translate-x-1/2 overflow-hidden rounded-t-[28px] sm:mb-24 sm:rounded-t-[40px] lg:mb-28" style={{ backgroundColor: "#050505", backgroundImage: RENTAI.band }}>
        {/* Top + side hairline stroke that fades out toward the bottom, so the side
            strokes dissolve before reaching the band's cut bottom edge. */}
        <span aria-hidden="true" className="pointer-events-none absolute inset-0 z-20 rounded-t-[28px] border border-b-0 border-white/15 sm:rounded-t-[40px]" style={{
            WebkitMaskImage: "linear-gradient(to bottom, #000 0%, #000 45%, transparent 80%)",
            maskImage: "linear-gradient(to bottom, #000 0%, #000 45%, transparent 80%)",
        }}/>
        <div className="relative z-10 mx-auto w-full max-w-7xl px-4 py-12 sm:px-6 sm:py-16 lg:py-20">
          {/* Brand logo (matched to the rentID card lockup size) with the Learn more link
            anchored to the top-right corner of the band, then the heading and supporting
            copy sitting side by side on one line. */}
          <div className="group/head flex flex-col gap-8 md:pl-10 min-[1440px]:pl-0">
            <div className="flex items-start justify-between gap-4">
              <Image src={RENTAI.logo || "/placeholder.svg"} alt={RENTAI.logoAlt} width={200} height={92} className={`w-auto -translate-y-[5px] object-contain object-left ${logoSize}`}/>
              {/* Suite marks (overlapping black-glass circles) with a Learn more link to their
            right. On hover the stack reveals a line describing what Rentii automates. */}
              <div className="group/marks hidden shrink-0 flex-col items-end gap-3 sm:flex">
                <SuiteMarksRow showLearnMore={!hideLM}/>
                <p className="max-w-xs font-sans text-sm leading-relaxed text-white/70 text-pretty opacity-0 transition-opacity duration-300 group-hover/marks:opacity-100 lg:text-right">
                  Automate your bookings, insurance, verifications and rental back office tasks.
                </p>
              </div>
            </div>
            <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:justify-between lg:gap-12">
              <div className="flex max-w-3xl flex-col gap-4">
                <h2 className="font-sans text-3xl font-normal leading-[1.05] tracking-tight text-white text-balance sm:text-4xl md:text-[44px]">
                  <span className="block sm:inline">Meet Rentii.</span>{" "}
                  {RENTAI.title.replace("Meet Rentii. ", "")}
                </h2>
                <p className="font-sans text-lg font-medium sm:text-xl leading-snug text-white text-pretty">
                  {RENTAI.lead}
                </p>
              </div>
              <div className="flex max-w-md flex-col gap-4 lg:text-right">
                <p className="font-sans text-lg leading-relaxed text-white/70 text-pretty">
                  <span className="font-semibold text-white">Pre-built rental workflow automations</span> to manage your
                  daily operations, logistics, and administrative tasks, while you stay in control of every decision.
                </p>
                {/* Mobile: the suite marks + Learn more sit under the subcopy instead of the header. */}
                {hideLM ? null : (<div className="sm:hidden">
                    <SuiteMarksRow showMarks={false} showLearnMore={!hideLM}/>
                  </div>)}
              </div>
            </div>
          </div>

          {/* Two cards side by side: a wide rentAI card (agent console) and a narrower
            rentSuccess card (video preview). Both stack media on top of text at the bottom
            and stretch to equal height. */}
          {/* hidden on phones: the agent card is sm+ only and the placeholder card is switched off,
              so below sm the row would be empty space. */}
          <div className="mt-10 hidden grid-cols-1 items-stretch gap-6 sm:mt-12 sm:grid lg:grid-cols-3">
            {/* rentAI — wide card spanning two columns. */}
            <div className="group relative isolate hidden flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-sm sm:flex sm:rounded-3xl mx-auto w-full max-w-5xl lg:col-span-3">
              {/* Media: the RentID Bookings portal sits as a smaller framed backdrop that stays
            visible the whole time, with the rentAI agent console floating on top of it
            (smaller than the portal). */}
              <div className="relative flex flex-1 items-center justify-center p-4 sm:p-6 lg:p-8">
                <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(55% 60% at 50% 45%, ${RENTAI.glow}, transparent 70%)` }}/>
                <div className="relative w-full overflow-hidden rounded-2xl border border-white/10 shadow-[0_18px_40px_rgba(0,0,0,0.55)]">
                  <Image src="/images/rentid-bookings-portal.webp" alt="RentID Bookings portal" width={1751} height={926} className="h-auto w-full" priority/>
                  {/* Subtle scrim so the floating chat panel reads clearly over the portal. */}
                  <span aria-hidden="true" className="pointer-events-none absolute inset-0 bg-black/30"/>
                  {/* rentAI agent console overlay: a cursor travels the portal to the rentAI
            icon and clicks it, then the chat panel floats on top (smaller than the
            portal). The preview fills the portal frame so the cursor can reach the
            sidebar icon. */}
                  <div className="absolute inset-0">
                    <RentAiAgentPreview />
                  </div>
                </div>
              </div>
            </div>

            {/* Right — media player only, with a title + single line of copy anchored at the
            bottom. SWITCHED OFF (owner, 2026-09-30) until the rentAI video exists: it was a
            placeholder, so the agent card above takes the centre instead. Flip RENTAI_VIDEO_CARD
            when the video is ready (and give the agent card back its lg:col-span-2). */}
            {RENTAI_VIDEO_CARD && (<div className="group relative isolate flex flex-col overflow-hidden rounded-2xl border border-white/10 bg-black/40 shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] backdrop-blur-sm sm:rounded-3xl">
              {/* Media: bare play button on a dark stage (no framed container). */}
              <div className="relative flex flex-1 items-center justify-center p-6 sm:p-10">
                <span aria-hidden="true" className="pointer-events-none absolute inset-0" style={{ background: `radial-gradient(60% 60% at 50% 45%, ${RENTAI.glow}, transparent 70%)` }}/>
                <span className="relative flex h-16 w-16 items-center justify-center rounded-full bg-white/10 ring-1 ring-white/15 backdrop-blur-sm transition-transform duration-300 group-hover:scale-105">
                  <Play aria-hidden="true" className="h-6 w-6 translate-x-[1px] fill-white/80 text-white/80"/>
                </span>
              </div>

              {/* Text: single line, at the bottom. */}
              <div className="bg-black/30 p-6 sm:p-8 lg:p-10">
                <p className="font-sans text-base leading-relaxed text-white/60 text-pretty sm:text-lg">
                  Hear how Rentii resolves incidents and collects funds.
                </p>
              </div>
            </div>)}
          </div>
          {inshur ? <InshurClaimCta className="mt-10 sm:mt-12"/> : null}
        </div>
      </div>
      )}

      {/* rentAcademy and the Rentid marketplace rendered as two separate cards sitting left and
            right of each other, rather than one card with the marketplace nested inside. */}
      {show("academy") && (
      <div id="rent-academy" className="grid scroll-mt-24 gap-6 lg:grid-cols-2 lg:items-stretch">
        {CARDS.map((card) => (<CardLink key={card.key} href={card.href} className="group relative isolate flex flex-col justify-start overflow-hidden rounded-2xl border-t border-white/20 p-6 transition-shadow hover:shadow-[0_24px_60px_-20px_rgba(0,0,0,0.6)] sm:rounded-3xl sm:p-8 lg:p-12" style={{
                backgroundColor: "#050505",
                backgroundImage: card.gradient,
            }}>
            <div className="relative z-10 flex flex-col">
              <Image src={card.logo || "/placeholder.svg"} alt={card.logoAlt} width={200} height={92} className="h-12 w-auto object-contain object-left sm:h-14"/>
              <h3 className="mt-8 font-sans text-3xl font-normal sm:text-4xl md:text-[42px] leading-[1.05] tracking-tight text-white text-balance">
                {card.title}
              </h3>
              <p className="mt-4 max-w-xl font-sans text-base leading-relaxed text-white/60 text-pretty sm:text-lg">
                {card.copy}
              </p>
              {!hideLM && (
              <span className="mt-7 inline-flex items-center gap-2 align-middle text-base font-normal text-white sm:text-lg">
                <span className="underline decoration-white/40 underline-offset-4 transition-colors group-hover:decoration-white">
                  Learn more
                </span>
                <ArrowRight aria-hidden="true" className="learn-more-arrow h-5 w-5 shrink-0"/>
              </span>
              )}
            </div>
          </CardLink>))}

        {/* Rentid marketplace — its own card beside the rentAcademy card, map bleeding
            edge to edge. */}
        <div className="relative flex overflow-hidden rounded-2xl sm:rounded-3xl">
          {/* Owner (2026-10-01): the "launching" badge on the home page too. */}
          <RentidMarketplaceCard comingSoon="Launching Mid-October 2026"/>
        </div>
      </div>
      )}
    </section>);
}
